'use client';

import { Cpu, Database, Zap, Shield } from 'lucide-react';

const TechnologyShowcase = () => {
  const technologies = [
    {
      icon: Cpu,
      title: 'AI-Powered Precision Analytics',
      description: 'Real-time emission monitoring and predictive modeling using advanced machine learning algorithms',
      capabilities: [
        'Pattern recognition for emission anomalies',
        'Automated MRV validation processes',
        'Predictive maintenance for sustainability systems',
        'Natural language processing for ESG reporting'
      ],
      color: 'from-blue-500 to-cyan-600'
    },
    {
      icon: Database,
      title: 'Quantum Computing Integration',
      description: 'Complex carbon sequestration calculations and verification processes using quantum algorithms',
      capabilities: [
        'Optimization of carbon offset portfolios',
        'Molecular-level carbon capture simulation',
        'Multi-variable climate impact modeling',
        'Cryptographic security for carbon credits'
      ],
      color: 'from-purple-500 to-pink-600'
    },
    {
      icon: Zap,
      title: 'Advanced Statistical Modeling',
      description: 'Accurate carbon credit valuation and market forecasting with statistical precision',
      capabilities: [
        'Time-series analysis for market trends',
        'Risk assessment and volatility prediction',
        'Correlation analysis across ESG factors',
        'Monte Carlo simulations for scenario planning'
      ],
      color: 'from-emerald-500 to-teal-600'
    },
    {
      icon: Shield,
      title: 'Algorithm-Driven Automation',
      description: 'Streamlined ESG compliance and reporting workflows with intelligent automation',
      capabilities: [
        'Automated data collection and validation',
        'Smart contract execution for compliance',
        'Real-time regulatory update tracking',
        'Intelligent document generation and filing'
      ],
      color: 'from-orange-500 to-red-600'
    }
  ];

  return (
    <section className="py-20 bg-slate-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Technology That Powers Results
          </h2>
          <p className="text-xl text-white/80 max-w-3xl mx-auto">
            From AI to Quantum — We have the math, statistics, and algorithms to accurately 
            measure, review, and validate your journey
          </p>
        </div>

        {/* Technology Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {technologies?.map((tech, index) => {
            const IconComponent = tech?.icon;
            return (
              <div
                key={index}
                className="group relative bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10 hover:border-white/20 transition-all duration-300"
              >
                {/* Gradient Background Effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${tech?.color} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300`} />
                {/* Icon */}
                <div className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${tech?.color} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <IconComponent className="w-8 h-8 text-white" />
                </div>
                {/* Content */}
                <h3 className="text-2xl font-bold text-white mb-4">{tech?.title}</h3>
                <p className="text-white/70 mb-6 leading-relaxed">{tech?.description}</p>
                {/* Capabilities List */}
                <div className="space-y-3">
                  {tech?.capabilities?.map((capability, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-white/80 text-sm">{capability}</span>
                    </div>
                  ))}
                </div>
                {/* Decorative Element */}
                <div className={`absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r ${tech?.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-b-2xl`} />
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col items-center gap-4 p-8 bg-white/5 backdrop-blur-lg rounded-2xl border border-white/10">
            <p className="text-2xl font-bold text-white">
              Ready to accelerate your sustainability journey?
            </p>
            <p className="text-white/70 max-w-2xl">
              Our advanced algorithms and AI-powered platform ensure accurate measurement, 
              validation, and acceleration of your ESG goals.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechnologyShowcase;